import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog.tsx';
import { toast } from 'sonner';
import type { CognitoUser } from '@metron/shared';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('DeleteUserDialog');

interface DeleteUserDialogProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onDeleteUser: (userId: string) => Promise<void>;
}

export function DeleteUserDialog({
  open,
  user,
  onOpenChange,
  onDeleteUser,
}: DeleteUserDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      await onDeleteUser(user.username);
      toast.success('User disabled successfully');
      onOpenChange(false);
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Failed to disable user');
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to disable user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disable User Account?</AlertDialogTitle>
          <AlertDialogDescription>
            This will disable the account for {user.name}{' '}
            ({user.email}). The user will not be able to log in until their account is re-enabled.
            <br />
            <br />
            This action does not permanently delete the user and can be reversed by editing the user
            and enabling their account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={isLoading}>
            {isLoading ? 'Disabling...' : 'Disable User'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
